import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import jwt_decode from 'jwt-decode';
import { filter, Observable, of } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectChatUsers, selectUser } from '../../../store/app.selectors';

import { skipMessagingKey } from '../../../core/services/http.context';
import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { IUserProfile } from '../../user/interfaces/IUserProfile';
import { IChannel } from '../interfaces/IChannel';
import { IMessage, Suggestion } from '../interfaces/IMessage';
import { IStandardResponse, IStandardResponseContext } from '../interfaces/IStandardResponse';
import { IWorkspace, WorkspaceFeatures } from '../interfaces/IWorkspace';
import { getStandardReplies } from '../store/chat.actions';
import { AppStateWithChat } from '../store/chat.reducer';
import { PermissionsService } from './permissions.service';

@Injectable()
export class StandardRepliesService {
  messages: IMessage[] = [];
  smartReplies: Suggestion[];
  isLoadingSmartReplies: boolean;
  selectedWorkSpace: IWorkspace | null = null;
  standardResponses: IStandardResponse[];
  user: IUserProfile | null = null;
  users$ = this.store.select(selectChatUsers);
  selectedChannel: IChannel | null = null;

  constructor(
    private env: MessagingEnvironmentService,
    private httpClient: HttpClient,
    private permissionsService: PermissionsService,
    private store: Store<AppStateWithChat>
  ) {
    this.setChatSubscription();
    this.setUserSubscription();
    this.setUsersSubscription();
  }

  setUserSubscription(): void {
    this.store.select(selectUser).subscribe((user) => {
      this.user = user;
    });
  }

  setUsersSubscription(): void {
    this.users$.subscribe((users) => {
      if (this.selectedChannel && users?.length) {
        this.store.dispatch(getStandardReplies({ channel: this.selectedChannel }));
      }
    });
  }

  setChatSubscription(): void {
    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ selectedWorkSpace, standardResponses, selectedChannel }) => {
        this.selectedWorkSpace = selectedWorkSpace;
        this.selectedChannel = selectedChannel;
        this.standardResponses = standardResponses;
      });
  }

  getAutoResponses(channel: IChannel): Observable<IStandardResponse[]> {
    if (!channel.enabled || !this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseAutoResponses])) {
      return of([]);
    }
    if (channel?.standardResponses?.length) {
      return of(this.standardResponses);
    }
    if (this.standardResponses?.length) {
      return of(this.standardResponses);
    }
    return this.httpClient.get<IStandardResponse[]>(
      `${this.env.env.ordersConversationApiUrlV2}/${this.getDealerID()}/dealer/responses`,
      {
        context: skipMessagingKey(),
      }
    );
  }

  getDealerID() {
    const token = localStorage.getItem('messaging-token');
    if (!token) {
      return '';
    }
    try {
      const decodedToken: { accountUid: string } = jwt_decode(token);
      return decodedToken?.accountUid ?? '';
    } catch {
      return '';
    }
  }

  getContext(channel: IChannel): IStandardResponseContext {
    let lastName = '';
    let firstName = '';
    if (channel.displayName) {
      const displayNamedSplited = channel.displayName.split(' ');
      firstName = displayNamedSplited[0];
      for (let index = 0; index < displayNamedSplited.length; index++) {
        if (index !== 0) {
          lastName = `${lastName}${displayNamedSplited[index]} `;
        }
      }
    }

    return {
      firstName,
      lastName,
      advisor: channel?.owner?.displayName || '',
      dealer: this.user?.subAccountMessageableEntity?.displayName || '',
      userPhone: this.user?.subjectMessageableEntity?.phoneNumber || '',
    };
  }
}
